import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

export interface FeedbackButtonProps {
  /** Optional analysis_id to associate with the feedback. */
  analysisId?: string;
  /** Visual variant. `link` is for inline placement; `chip` for footer. */
  variant?: 'link' | 'chip';
  label?: string;
}

const MAX_CHARS = 2000;

export function FeedbackButton({
  analysisId,
  variant = 'chip',
  label = 'Have feedback?',
}: FeedbackButtonProps) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.submitFeedback({
        message: message.trim(),
        ...(analysisId ? { analysis_id: analysisId } : {}),
      }),
    onSuccess: () => {
      setSubmitted(true);
      setMessage('');
    },
  });

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeDialog();
    }
    function onDoc(e: MouseEvent) {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        closeDialog();
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDoc);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDoc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function openDialog() {
    if (!auth.isAuthenticated) {
      auth.signinRedirect();
      return;
    }
    setSubmitted(false);
    mutation.reset();
    setOpen(true);
  }

  function closeDialog() {
    setOpen(false);
    setSubmitted(false);
    mutation.reset();
  }

  const trimmedLength = message.trim().length;
  const overLimit = message.length > MAX_CHARS;
  const canSubmit = trimmedLength > 0 && !overLimit && !mutation.isPending;

  const errorMessage =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error
        ? 'Something went wrong. Try again.'
        : null;

  const triggerClass =
    variant === 'link'
      ? 'text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline'
      : 'rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted';

  return (
    <>
      <button type="button" onClick={openDialog} className={triggerClass}>
        {label}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Send feedback"
        >
          <div
            ref={dialogRef}
            className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-xl"
          >
            <h2 className="text-lg font-semibold">
              {analysisId ? 'Feedback on this result' : 'Send feedback'}
            </h2>
            {!submitted && (
              <p className="mt-1 text-sm text-muted-foreground">
                {analysisId
                  ? "What did we get right or wrong on this score? We read every note."
                  : 'Tell us what you think — anything from a bug to an idea. We read every note.'}
              </p>
            )}

            {submitted ? (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-foreground">Got it — thanks for the note. 🐟</p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeDialog}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!canSubmit) return;
                  mutation.mutate();
                }}
                className="mt-4 space-y-3"
              >
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  autoFocus
                  placeholder={analysisId ? 'e.g. The bias score feels too low — the piece is clearly opinion.' : 'Your feedback…'}
                  className="block w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={mutation.isPending}
                />
                <div className="flex items-center justify-between text-xs">
                  <span className={overLimit ? 'text-destructive' : 'text-muted-foreground'}>
                    {message.length}/{MAX_CHARS}
                  </span>
                  {errorMessage && <span className="text-destructive">{errorMessage}</span>}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeDialog}
                    className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
                    disabled={mutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
                  >
                    {mutation.isPending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
